import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SetPasswordForm } from "@/components/SetPasswordForm";
import { useAuth } from "@/auth/AuthProvider";
import {
  isPasskeySupported,
  registerPasskey,
  describePasskeyError,
} from "@/lib/passkeys";
import { PageHeader } from "@/components/PageHeader";

// The guided "secure your account" interstitial (BRK-S026). Shown by
// SecurityOnboardingGate after a login when the broker has no strong factor
// (no passkey, no MFA). It LEADS with a passkey — phishing-resistant and needing
// no password — then offers an authenticator app (which needs a password, set on
// /security), an optional set-a-password step, and an always-present skip. Every
// path is skippable; a password alone does NOT end the nudge — the broker is
// prompted again next session until a passkey or MFA exists.
//
// Reuses the existing passkey ceremony (registerPasskey) and the shared
// SetPasswordForm rather than duplicating either (DRY). TOTP enrollment lives on
// /security, so the authenticator option hands off there.

const recommendedBtn =
  "flex w-full items-center justify-between gap-3 rounded-md bg-brk-accent px-5 py-3 text-left font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const secondaryBtn =
  "flex w-full items-center justify-between gap-3 rounded-md border border-brk-border px-5 py-3 text-left font-medium text-brk-accent transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60";

export function SecurityOnboarding() {
  const { posture, authedRequest, refreshPosture, dismissSecurityPrompt } =
    useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<"choices" | "set-password">("choices");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const hasPassword = posture?.hasPassword ?? false;
  const passkeySupported = isPasskeySupported();

  // A passkey is a strong factor: registering one flips needsSecurityOnboarding
  // off, so refreshing the identity lets the gate render the page underneath.
  async function onAddPasskey() {
    setError(null);
    setPending(true);
    try {
      await registerPasskey(authedRequest);
      await refreshPosture();
      dismissSecurityPrompt();
    } catch (err) {
      // A cancelled ceremony resets quietly; a real failure surfaces inline.
      const message = describePasskeyError(err);
      if (message) setError(message);
    } finally {
      setPending(false);
    }
  }

  // The authenticator app needs a password as its first factor, and TOTP setup
  // lives on /security — dismiss for the session and hand off there.
  function onUseAuthenticator() {
    dismissSecurityPrompt();
    navigate("/security");
  }

  // Setting a password alone is NOT a strong factor; refresh so /security and a
  // later prompt see hasPassword, dismiss for this session, and let them through.
  async function onPasswordSet() {
    await refreshPosture();
    dismissSecurityPrompt();
  }

  return (
    <>
      <PageHeader
        title="Secure your account"
        meta="Add a stronger way to sign in. It only takes a moment, and you can always do it later."
      />
      {/* The choices keep a 448px column: each is a full-width button, and at
          the form width one would read as a banner (BRK-S262). */}
      <section className="flex max-w-md flex-col gap-6">
        {view === "set-password" ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-brk-dim">
              Choose a password for your account.
            </p>
            <SetPasswordForm
              mode="set"
              request={authedRequest}
              onSuccess={onPasswordSet}
            />
            <button
              type="button"
              onClick={() => {
                setView("choices");
                setError(null);
              }}
              className="self-start text-sm text-brk-accent hover:underline"
            >
              ← Back
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {error && (
              <p role="alert" className="text-sm text-brk-danger">
                {error}
              </p>
            )}

            {passkeySupported && (
              <button
                type="button"
                onClick={onAddPasskey}
                disabled={pending}
                className={recommendedBtn}
              >
                <span className="flex flex-col">
                  <span>Add a passkey</span>
                  <span className="text-xs font-normal text-white/80">
                    Sign in with your fingerprint, face, or device
                  </span>
                </span>
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium">
                  Recommended
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={onUseAuthenticator}
              disabled={pending}
              className={secondaryBtn}
            >
              <span className="flex flex-col">
                <span>Use an authenticator app</span>
                <span className="text-xs font-normal text-brk-dim">
                  A 6-digit code at sign-in (needs a password)
                </span>
              </span>
            </button>

            {!hasPassword && (
              <button
                type="button"
                onClick={() => {
                  setView("set-password");
                  setError(null);
                }}
                disabled={pending}
                className={secondaryBtn}
              >
                <span className="flex flex-col">
                  <span>Set a password</span>
                  <span className="text-xs font-normal text-brk-dim">
                    Sign in with an email and password
                  </span>
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={dismissSecurityPrompt}
              disabled={pending}
              className="self-center pt-1 text-sm text-brk-dim hover:text-brk-text hover:underline disabled:opacity-60"
            >
              I’ll do this later
            </button>
          </div>
        )}
      </section>
    </>
  );
}
